import axios, { type AxiosRequestConfig } from 'axios'
import { API_ROUTES } from './constants'
import type {
  ApiResponse,
  LoginCredentials,
  RegisterData,
  User,
  ShopifyAnalytics,
  ShopifyProduct,
  ShopifyOrder,
  ShopifyCustomer,
  Payment,
  PaymentMethod,
  ChatMessage,
  AIContext,
  ContentType,
  ContentItem,
  PaginatedResponse,
  WizardProgress,
} from '@/types'

const api = axios.create({
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('droplaunch_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('droplaunch_token')
      localStorage.removeItem('droplaunch_user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const res = await api.request<ApiResponse<T>>(config)
  if (!res.data.success) {
    throw new Error(res.data.error ?? 'Request failed')
  }
  return res.data.data as T
}

// ====================================
// Auth API
// ====================================
export const authApi = {
  login: (credentials: LoginCredentials) =>
    request<{ user: User; token: string }>({
      method: 'POST',
      url: API_ROUTES.auth.login,
      data: credentials,
    }),

  register: (data: RegisterData) =>
    request<{ user: User; token: string }>({
      method: 'POST',
      url: API_ROUTES.auth.register,
      data,
    }),

  logout: () =>
    request<void>({ method: 'POST', url: API_ROUTES.auth.logout }),

  me: () =>
    request<User>({ method: 'GET', url: API_ROUTES.auth.me }),

  updateProfile: (data: Partial<RegisterData>) =>
    request<User>({ method: 'PATCH', url: API_ROUTES.auth.me, data }),
}

// ====================================
// Shopify API
// ====================================
export const shopifyApi = {
  getAnalytics: () =>
    request<ShopifyAnalytics>({ method: 'GET', url: API_ROUTES.shopify.analytics }),

  getProducts: (params?: {
    page?: number
    pageSize?: number
    status?: string
    search?: string
    collectionId?: string
    cursor?: string
  }) =>
    request<PaginatedResponse<ShopifyProduct>>({
      method: 'GET',
      url: API_ROUTES.shopify.products,
      params,
    }),

  getProduct: (id: string) =>
    request<ShopifyProduct>({ method: 'GET', url: `${API_ROUTES.shopify.products}/${id}` }),

  createProduct: (data: Partial<ShopifyProduct>) =>
    request<ShopifyProduct>({
      method: 'POST',
      url: API_ROUTES.shopify.products,
      data,
    }),

  updateProduct: (id: string, data: Partial<ShopifyProduct>) =>
    request<ShopifyProduct>({
      method: 'PATCH',
      url: `${API_ROUTES.shopify.products}/${id}`,
      data,
    }),

  deleteProduct: (id: string) =>
    request<void>({ method: 'DELETE', url: `${API_ROUTES.shopify.products}/${id}` }),

  bulkUpdateProducts: (ids: string[], action: string) =>
    request<void>({
      method: 'POST',
      url: `${API_ROUTES.shopify.products}/bulk`,
      data: { ids, action },
    }),

  getOrders: (params?: {
    page?: number
    status?: string
    search?: string
    dateRange?: string
    riskLevel?: string
    cursor?: string
  }) =>
    request<PaginatedResponse<ShopifyOrder>>({
      method: 'GET',
      url: API_ROUTES.shopify.orders,
      params,
    }),

  getOrder: (id: string) =>
    request<ShopifyOrder>({ method: 'GET', url: `${API_ROUTES.shopify.orders}/${id}` }),

  fulfillOrder: (id: string) =>
    request<void>({
      method: 'POST',
      url: `${API_ROUTES.shopify.orders}/${id}/fulfill`,
    }),

  cancelOrder: (id: string, reason?: string) =>
    request<void>({
      method: 'POST',
      url: `${API_ROUTES.shopify.orders}/${id}/cancel`,
      data: { reason },
    }),

  getCustomers: (params?: { page?: number; search?: string; cursor?: string }) =>
    request<PaginatedResponse<ShopifyCustomer>>({
      method: 'GET',
      url: API_ROUTES.shopify.customers,
      params,
    }),

  getStoreInfo: () =>
    request<{ name: string; email: string; currency: string; domain: string }>({
      method: 'GET',
      url: API_ROUTES.shopify.store,
    }),

  updateStoreBasics: (data: { name?: string; email?: string; metaTitle?: string; metaDescription?: string }) =>
    request<void>({ method: 'PATCH', url: API_ROUTES.shopify.store, data }),

  createPage: (data: { title: string; body: string; handle?: string; published?: boolean }) =>
    request<{ id: string; handle: string }>({
      method: 'POST',
      url: API_ROUTES.shopify.pages,
      data,
    }),

  updateMenu: (menuHandle: string, items: { title: string; url: string }[]) =>
    request<void>({
      method: 'PUT',
      url: `${API_ROUTES.shopify.menus}/${menuHandle}`,
      data: { items },
    }),
}

// ====================================
// Claude AI API
// ====================================
export const claudeApi = {
  chat: async (
    messages: Pick<ChatMessage, 'role' | 'content'>[],
    context?: AIContext,
    onChunk?: (chunk: string) => void
  ): Promise<string> => {
    const res = await fetch(API_ROUTES.claude.chat, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('droplaunch_token')}`,
      },
      body: JSON.stringify({ messages, context }),
    })

    if (!res.ok) throw new Error('AI request failed')
    if (!res.body) throw new Error('No response body')

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let fullText = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      const chunk = decoder.decode(value, { stream: true })
      fullText += chunk
      onChunk?.(chunk)
    }

    return fullText
  },

  generate: (type: string, data: Record<string, unknown>) =>
    request<{ content: string }>({
      method: 'POST',
      url: API_ROUTES.claude.generate,
      data: { type, ...data },
    }),

  analyze: (type: string, data: Record<string, unknown>) =>
    request<{ analysis: string; suggestions: string[] }>({
      method: 'POST',
      url: API_ROUTES.claude.analyze,
      data: { type, ...data },
    }),
}

// ====================================
// Payments API
// ====================================
export const paymentsApi = {
  submitPayment: (data: { method: PaymentMethod; amount: string; txHash?: string }) =>
    request<Payment>({ method: 'POST', url: API_ROUTES.payments.submit, data }),

  getHistory: () =>
    request<Payment[]>({ method: 'GET', url: API_ROUTES.payments.history }),

  uploadScreenshot: async (file: File): Promise<string> => {
    const formData = new FormData()
    formData.append('screenshot', file)
    const res = await api.post<ApiResponse<{ url: string }>>(API_ROUTES.payments.upload, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data.data?.url ?? ''
  },
}

// ====================================
// Wizard & Progress API
// ====================================
export const wizardApi = {
  getProgress: () =>
    request<WizardProgress>({ method: 'GET', url: '/api/wizard/progress' }),

  completeStep: (step: number, data?: Record<string, unknown>) =>
    request<WizardProgress>({
      method: 'POST',
      url: `/api/wizard/step/${step}`,
      data,
    }),
}

// ====================================
// Content Library API
// ====================================
export const contentApi = {
  save: (data: { type: ContentType | string; title: string; content: string; metadata?: Record<string, unknown> }) =>
    request<{ id: string }>({ method: 'POST', url: '/api/content', data }),

  list: (type?: ContentType | string) =>
    request<{ items: ContentItem[]; total: number }>({
      method: 'GET',
      url: '/api/content',
      params: type ? { type } : undefined,
    }),

  delete: (id: string) =>
    request<void>({ method: 'DELETE', url: `/api/content/${id}` }),
}

// ====================================
// FAQ API
// ====================================
export const faqApi = {
  exportToShopify: (title: string, html: string) =>
    request<{ page: { id: number; handle: string } }>({ method: 'POST', url: '/api/faq/export', data: { title, html } }),
}

// ====================================
// Checklist API
// ====================================
export const checklistApi = {
  getCompleted: () =>
    request<{ completedItems: string[] }>({ method: 'GET', url: '/api/checklist' }),

  toggleItem: (itemId: string, completed: boolean) =>
    request<{ success: boolean }>({ method: 'POST', url: '/api/checklist', data: { itemId, completed } }),

  reset: () =>
    request<{ success: boolean }>({ method: 'DELETE', url: '/api/checklist' }),
}


// ====================================
// Admin API
// ====================================
export const adminApi = {
  getUsers: () => request<User[]>({ method: 'GET', url: API_ROUTES.admin.users }),

  getPayments: () =>
    request<Array<Payment & { userName: string; userEmail: string }>>({
      method: 'GET',
      url: API_ROUTES.admin.payments,
    }),

  verifyPayment: (id: string, status: 'approved' | 'rejected', notes?: string) =>
    request<void>({
      method: 'PATCH',
      url: `${API_ROUTES.admin.payments}/${id}`,
      data: { status, notes },
    }),

  extendTrial: (userId: string, days: number) =>
    request<void>({
      method: 'POST',
      url: `${API_ROUTES.admin.users}/${userId}/extend-trial`,
      data: { days },
    }),

  sendBroadcast: (title: string, body: string) =>
    request<void>({
      method: 'POST',
      url: API_ROUTES.admin.broadcast,
      data: { title, body },
    }),

  getStats: () =>
    request<{ totalUsers: number; activeSubscribers: number; trialUsers: number; mrr: number }>({
      method: 'GET',
      url: API_ROUTES.admin.stats,
    }),
}

export default api
